import { definePreset } from '@primeuix/themes';
import Aura from '@primeuix/themes/aura';

const sweetBlue = {
  50: '#d7efff',
  100: '#b8e2ff',
  200: '#8fd0ff',
  300: '#5eb8f5',
  400: '#2a9ee8',
  500: '#0083d3',
  600: '#006db3',
  700: '#0057a1',
  800: '#004280',
  900: '#002f5c',
  950: '#001a33',
};

const sweetNeutral = {
  0: '#ffffff',
  50: '#fbfcfe',
  100: '#f5f6f9',
  200: '#e2e8f4',
  300: '#dfe1e7',
  400: '#c6cbd4',
  500: '#848994',
  600: '#505868',
  700: '#151e35',
  800: '#182543',
  900: '#121a2e',
  950: '#0a0f1a',
};

const sweetRed = {
  50: '#fff1ef',
  100: '#ffe5e1',
  200: '#ffcac4',
  300: '#f2a29c',
  400: '#e97570',
  500: '#d94543',
  600: '#c73a38',
  700: '#a52e2c',
  800: '#7d2220',
  900: '#551714',
  950: '#2e0b0a',
};

export const SweetPreset = definePreset(Aura, {
  primitive: {
    borderRadius: {
      none: '0',
      xs: '4px',
      sm: '8px',
      md: '12px',
      lg: '16px',
      xl: '20px',
    },
    red: sweetRed,
  },
  semantic: {
    primary: {
      50: sweetBlue[50],
      100: sweetBlue[100],
      200: sweetBlue[200],
      300: sweetBlue[300],
      400: sweetBlue[400],
      500: sweetBlue[500],
      600: sweetBlue[600],
      700: sweetBlue[700],
      800: sweetBlue[800],
      900: sweetBlue[900],
      950: sweetBlue[950],
    },
    focusRing: {
      width: '0',
      style: 'none',
      color: 'transparent',
      offset: '0',
      shadow: '0 0 0 3px #d7efff',
    },
    formField: {
      paddingX: '14px',
      paddingY: '11px',
      borderRadius: '12px',
    },
    colorScheme: {
      light: {
        surface: sweetNeutral,
        primary: {
          color: '#0083d3',
          contrastColor: '#ffffff',
          hoverColor: '#0057a1',
          activeColor: '#004280',
        },
        text: {
          color: '#151e35',
          hoverColor: '#151e35',
          mutedColor: '#505868',
          hoverMutedColor: '#505868',
        },
        formField: {
          background: '#ffffff',
          disabledBackground: '#f5f6f9',
          filledBackground: '#f5f6f9',
          filledHoverBackground: '#f5f6f9',
          filledFocusBackground: '#ffffff',
          borderColor: '#dfe1e7',
          hoverBorderColor: '#c6cbd4',
          focusBorderColor: '#0083d3',
          invalidBorderColor: '#d94543',
          color: '#151e35',
          disabledColor: '#848994',
          placeholderColor: '#848994',
          floatLabelColor: '#848994',
          floatLabelFocusColor: '#0083d3',
          iconColor: '#848994',
        },
        content: {
          background: '#ffffff',
          hoverBackground: '#f5f6f9',
          borderColor: '#dfe1e7',
          color: '#151e35',
          hoverColor: '#151e35',
        },
        highlight: {
          background: '#d7efff',
          focusBackground: '#b8e2ff',
          color: '#0057a1',
          focusColor: '#004280',
        },
        mask: {
          background: 'rgba(24, 37, 67, 0.4)',
          color: '#f5f6f9',
        },
      },
    },
  },
  components: {
    button: {
      root: {
        borderRadius: '12px',
        paddingX: '20px',
        paddingY: '11px',
        label: {
          fontWeight: '600',
        },
      },
    },
    inputtext: {
      root: {
        paddingX: '14px',
        paddingY: '11px',
        borderRadius: '12px',
      },
    },
    password: {
      icon: {
        color: '#848994',
      },
    },
    select: {
      root: {
        borderRadius: '12px',
      },
      overlay: {
        borderRadius: '12px',
      },
    },
    checkbox: {
      root: {
        borderRadius: '4px',
      },
    },
    radiobutton: {},
    datatable: {
      headerCell: {
        padding: '8px 16px',
        background: '#f5f6f9',
        color: '#505868',
        borderColor: '#dfe1e7',
      },
      bodyCell: {
        padding: '16px',
        borderColor: '#dfe1e7',
      },
    },
    dialog: {
      root: {
        borderRadius: '20px',
        shadow: '0 16px 40px rgba(24, 37, 67, 0.1), 0 4px 12px rgba(24, 37, 67, 0.06)',
      },
      header: {
        padding: '24px',
      },
      content: {
        padding: '0 24px 24px',
      },
    },
    drawer: {
      root: {
        shadow: '0 16px 40px rgba(24, 37, 67, 0.1), 0 4px 12px rgba(24, 37, 67, 0.06)',
      },
      header: {
        padding: '24px 16px',
      },
      content: {
        padding: '16px',
      },
    },
    toast: {
      root: {
        borderRadius: '12px',
      },
      content: {
        padding: '16px',
      },
      summary: {
        fontWeight: '600',
      },
    },
    menu: {
      root: {
        borderRadius: '12px',
        shadow: '0 8px 24px rgba(24, 37, 67, 0.08), 0 2px 6px rgba(24, 37, 67, 0.05)',
      },
      item: {
        borderRadius: '8px',
        padding: '8px 12px',
      },
    },
    tabs: {
      tablist: {
        borderColor: '#dfe1e7',
      },
      tab: {
        fontWeight: '600',
        activeColor: '#0057a1',
      },
    },
    paginator: {
      root: {
        padding: '8px 16px',
      },
    },
    card: {
      root: {
        borderRadius: '20px',
        shadow: '0 8px 24px rgba(24, 37, 67, 0.08), 0 2px 6px rgba(24, 37, 67, 0.05)',
      },
      body: {
        padding: '24px',
      },
      title: {
        fontSize: '19px',
        fontWeight: '700',
      },
    },
    tooltip: {
      root: {
        borderRadius: '8px',
        background: '#182543',
        color: '#ffffff',
        padding: '8px 12px',
      },
    },
  },
});
